import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Checkbox,
  Typography,
  Box,
  Chip,
  IconButton,
  Grid,
  CardActionArea,
  LinearProgress,
} from '@mui/material';
import {
  Computer,
  Wifi,
  Schedule,
  SignalWifi4Bar,
  SignalWifi3Bar,
  SignalWifi2Bar,
  SignalWifi1Bar,
  SignalWifiOff,
  Info,
  SystemUpdate,
  Thermostat,
} from '@mui/icons-material';
import { Device, Task, isDeviceOnline } from '../types';
import { ChannelChip } from '../devices/ChannelChip';
import { taskApi } from '../api';
import DeviceNameEditor from './DeviceNameEditor';
import { formatBytes, formatRelativeTime, useI18n } from '../i18n';
import { controllerDetailPath } from '../nav/paths';

interface DeviceCardProps {
  device: Device;
  onUpdated?: (device: Device) => void;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
}

const DeviceCard: React.FC<DeviceCardProps> = ({ device, onUpdated, selected = false, onSelectedChange }) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [activeOTATask, setActiveOTATask] = useState<Task | null>(null);
  const getSignalIcon = (rssi: number) => {
    if (rssi >= -50) return <SignalWifi4Bar color="success" />;
    if (rssi >= -60) return <SignalWifi3Bar color="success" />;
    if (rssi >= -70) return <SignalWifi2Bar color="warning" />;
    if (rssi >= -80) return <SignalWifi1Bar color="error" />;
    return <SignalWifiOff color="disabled" />;
  };

  const getSignalColor = (rssi: number) => {
    if (rssi >= -50) return 'success';
    if (rssi >= -60) return 'success';
    if (rssi >= -70) return 'warning';
    return 'error';
  };

  const isOnline = isDeviceOnline(device);

  // Helper functions for OTA detection and progress
  const isOTATask = (task: Task): boolean => {
    return task.command.toLowerCase().includes('update') || 
           task.command.toLowerCase().includes('ota') ||
           task.command.toLowerCase().includes('.bin');
  };

  const getOTAProgress = (task: Task): { percentage: number; status?: string; currentSize?: number; totalSize?: number } | null => {
    if (!isOTATask(task) || !task.result) return null;
    
    if (typeof task.result === 'object' && task.result.percentage !== undefined) {
      return {
        percentage: task.result.percentage || 0,
        status: task.result.status,
        currentSize: task.result.currentSize,
        totalSize: task.result.totalSize
      };
    }
    
    return null;
  };

  // Load active OTA tasks
  useEffect(() => {
    if (!device.id || !isOnline) {
      setActiveOTATask(null);
      return;
    }

    const loadOTATasks = async () => {
      try {
        const tasks = await taskApi.getTasks(device.id!);
        const runningOTATask = tasks.find(task => 
          isOTATask(task) && (task.state === 'running' || task.state === 'pending')
        );
        setActiveOTATask(runningOTATask || null);
      } catch (error) {
        // Silently fail - this is just for display enhancement
        setActiveOTATask(null);
      }
    };

    loadOTATasks();
    // Check for OTA tasks every 5 seconds if device is online
    const interval = setInterval(loadOTATasks, 5000);
    return () => clearInterval(interval);
  }, [device.id, isOnline]);

  const handleCardClick = () => {
    if (device.id) {
      navigate(controllerDetailPath(device.id));
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box display="flex" alignItems="flex-start">
        {onSelectedChange && (
          <Box sx={{ pt: 1.25, pl: 0.5 }} onClick={(e) => e.stopPropagation()}>
            <Checkbox
              size="small"
              checked={selected}
              onChange={(e) => onSelectedChange(e.target.checked)}
              inputProps={{ 'aria-label': t('table.selectRow') }}
            />
          </Box>
        )}
        <CardActionArea onClick={handleCardClick} sx={{ flexGrow: 1 }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box display="flex" alignItems="flex-start" gap={1} sx={{ minWidth: 0, flex: 1, mr: 1 }}>
              <Computer color="primary" sx={{ mt: 0.5 }} />
              <Box sx={{ minWidth: 0 }}>
                <DeviceNameEditor device={device} variant="h6" onUpdated={onUpdated} />
              </Box>
            </Box>
            <Box display="flex" gap={0.5} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
              <Chip 
                label={isOnline ? t('common.online') : t('common.offline')} 
                color={isOnline ? 'success' : 'default'}
                size="small"
              />
              <ChannelChip device={device} />
            </Box>
          </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              {t('devices.modelValue', { model: device.model })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
              {t('devices.firmwareValue', { firmware: device.firmware || device.version })}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <Wifi />
              <Typography variant="body2">
                {device.ip}
              </Typography>
            </Box>
          </Grid>

          {device.rssi && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                {getSignalIcon(device.rssi)}
                <Typography variant="body2">
                  {t('devices.rssiValue')} 
                  <Chip 
                    label={`${device.rssi} dBm`} 
                    size="small" 
                    color={getSignalColor(device.rssi) as any}
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Box>
            </Grid>
          )}

          {device.mac && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {t('devices.macValue', { mac: device.mac })}
              </Typography>
            </Grid>
          )}

          {device.temperature !== undefined && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <Thermostat />
                <Typography variant="body2">
                  {t('devices.temperatureValue')} 
                  <Chip 
                    label={`${device.temperature.toFixed(1)}°C`} 
                    size="small" 
                    color={device.temperature > 70 ? 'error' : device.temperature > 60 ? 'warning' : 'default'}
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Box>
            </Grid>
          )}

          {/* OTA Progress Section */}
          {activeOTATask && (() => {
            const otaProgress = getOTAProgress(activeOTATask);
            if (otaProgress) {
              return (
                <Grid item xs={12}>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <SystemUpdate color="primary" fontSize="small" />
                      <Typography variant="body2" color="primary" fontWeight="medium">
                        {t('devices.otaInProgress')}
                      </Typography>
                      <Chip 
                        label={`${otaProgress.percentage}%`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={otaProgress.percentage} 
                      color="primary"
                      sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {otaProgress.status || t('devices.updatingFirmware')}
                      {otaProgress.currentSize && otaProgress.totalSize && 
                        ` (${formatBytes(otaProgress.currentSize)}/${formatBytes(otaProgress.totalSize)})`
                      }
                    </Typography>
                  </Box>
                </Grid>
              );
            }
            return null;
          })()}

          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1} justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                <Schedule />
                <Typography variant="body2" color="text.secondary">
                  {t('devices.lastSeenValue', { time: formatRelativeTime(device.lastSeen) })}
                </Typography>
              </Box>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardClick();
                }}
                sx={{ color: 'primary.main' }}
                title={t('devices.viewDetails')}
              >
                <Info fontSize="small" />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
        </CardContent>
        </CardActionArea>
      </Box>
    </Card>
  );
};

export default DeviceCard;
